import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import VocabularyList from './dsl-components/VocabularyList';
import { AppTab } from './appLayoutConfig';
import './language-learning-wrapper.scss';

interface LanguageLearningWrapperProps {
  languageLearningId: string;
  threadType: 'vocab' | 'learning' | 'reading';
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

interface LanguageLearningData {
  language: string;
  cefrLevel: string;
}

/** Tabs to show for vocabulary threads */
export const VOCAB_THREAD_TABS: AppTab[] = [
  { id: 'vocabulary', label: 'Vocabulary' },
  { id: 'grammar', label: 'Grammar' },
  { id: 'overdue', label: 'Overdue' },
];

/** Tabs to show for learning threads */
export const LEARNING_THREAD_TABS: AppTab[] = [
  { id: 'lessons', label: 'Lessons' },
  { id: 'progress', label: 'Progress' },
];

/** Tabs to show for reading threads */
export const READING_THREAD_TABS: AppTab[] = [
  { id: 'vocabulary', label: 'Vocabulary' },
  { id: 'articles', label: 'Articles' },
];

/**
 * Wrapper component for Language Learning threads.
 * Shows different tabs and content based on threadType (vocab, learning, reading).
 */
const LanguageLearningWrapper: React.FC<LanguageLearningWrapperProps> = ({
  languageLearningId,
  threadType,
  threadId,
  tabId,
  updateThreadViewVisualDescription,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [languageData, setLanguageData] = useState<LanguageLearningData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');

  // Determine which tabs to show based on thread type
  const getTabsForThreadType = useCallback((): AppTab[] => {
    switch (threadType) {
      case 'vocab':
        return VOCAB_THREAD_TABS;
      case 'learning':
        return LEARNING_THREAD_TABS;
      case 'reading':
        return READING_THREAD_TABS;
      default:
        return [];
    }
  }, [threadType]);

  // Get default tab for thread type
  const getDefaultTab = useCallback((): string => {
    switch (threadType) {
      case 'vocab':
        return 'vocabulary';
      case 'learning':
        return 'lessons';
      case 'reading':
        return 'vocabulary';
      default:
        return 'vocabulary';
    }
  }, [threadType]);

  const effectiveTabId = tabId || getDefaultTab();

  // Fetch language learning data
  const fetchLanguageData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch vocabulary progress to get language info
      const response = await api.get('/language-learning/vocabulary', {
        params: {
          languageLearningId,
          limit: 1,
        },
      });

      if (response.data.success) {
        setLanguageData({
          language: response.data.language || 'Language',
          cefrLevel: response.data.cefrLevel || '',
        });
      }
    } catch (err) {
      console.error('Error fetching language data:', err);
      setError('Failed to load language data');
    } finally {
      setLoading(false);
    }
  }, [languageLearningId, getAccessTokenSilently]);

  // Reset state when languageLearningId or threadType changes
  useEffect(() => {
    setLoading(true);
    setLanguageData(null);
    setError(null);
    lastTabsRef.current = ''; // Reset so onTabsReady will be called
    fetchLanguageData();
  }, [languageLearningId, threadType, fetchLanguageData]);

  // Notify parent of tabs when data is loaded
  useEffect(() => {
    if (loading || !onTabsReady) return;

    const tabs = getTabsForThreadType();
    const tabsKey = JSON.stringify(tabs);

    // Only call if tabs actually changed
    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [loading, threadType, onTabsReady, getTabsForThreadType]);

  if (loading) {
    return <div className='language-learning-wrapper loading'></div>;
  }

  if (error) {
    return null;
  }

  // Render content based on thread type and tab
  const renderContent = () => {
    if (threadType === 'vocab') {
      switch (effectiveTabId) {
        case 'vocabulary':
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='vocabulary'
              title={
                languageData
                  ? `${languageData.language} Vocabulary`
                  : 'Vocabulary'
              }
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
        case 'grammar':
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='grammar'
              title={
                languageData ? `${languageData.language} Grammar` : 'Grammar'
              }
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
        case 'overdue':
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='all'
              title='Overdue Items'
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
        default:
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='vocabulary'
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
      }
    }

    if (threadType === 'reading') {
      switch (effectiveTabId) {
        case 'vocabulary':
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='all'
              title={
                languageData
                  ? `${languageData.language} Reading Vocabulary`
                  : 'Reading Vocabulary'
              }
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
        case 'articles':
          // Placeholder for future articles feature
          return (
            <div className='placeholder-content'>
              <p>Reading articles will appear here.</p>
            </div>
          );
        default:
          return null;
      }
    }

    if (threadType === 'learning') {
      // Learning thread - placeholder for now
      switch (effectiveTabId) {
        case 'lessons':
          return (
            <div className='placeholder-content'>
              <p>Lessons and exercises will appear here.</p>
            </div>
          );
        case 'progress':
          return (
            <VocabularyList
              languageLearningId={languageLearningId}
              itemType='all'
              title='Learning Progress'
              threadId={threadId}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          );
        default:
          return null;
      }
    }

    return null;
  };

  return <div className='language-learning-wrapper'>{renderContent()}</div>;
};

export default LanguageLearningWrapper;
